import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { AlertTriangle, Plus, Trash2, Download, X, Pencil, Mail, MailCheck, CheckCircle2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmDialog from '../DeleteConfirmDialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WARNING_BADGES = {
  verbal: 'bg-yellow-500', written: 'bg-orange-500', written2: 'bg-orange-600', final: 'bg-red-600',
};
const WARNING_LABEL = {
  verbal: 'Verbal Warning', written: 'Written Warning', written2: 'Written Warning #2', final: 'Final Warning',
};

const emptyForm = () => ({
  id: null,
  issue_date: new Date().toISOString().split('T')[0],
  company: 'stars_n_bars',
  warning_type: 'written',
  offense_types: [],
  custom_offenses: [],
  description: '',
  plan_for_improvement: '',
  disciplinary_action: '',
  manager: '',
  issued_by: '',
  suspension_enabled: false,
  suspension_days: '',
  suspension_from: '',
  send_email: false,
});

const WarningsDialog = ({ open, onOpenChange, employee, onSuccess, isViewer = false }) => {
  const [warnings, setWarnings] = useState([]);
  const [suspensions, setSuspensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [warningToDelete, setWarningToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [offenseTypes, setOffenseTypes] = useState([]);
  const [warningTypes, setWarningTypes] = useState([]);
  const [customInput, setCustomInput] = useState('');
  const [form, setForm] = useState(emptyForm());

  const loadWarnings = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/employees/${employee.id}/warnings`),
        axios.get(`${API_URL}/api/attendance/suspension/${employee.id}`).catch(() => ({ data: [] })),
      ]);
      setWarnings(wRes.data || []);
      setSuspensions(sRes.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load warning letters', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [employee]);

  useEffect(() => {
    if (open && employee) {
      loadWarnings();
      axios.get(`${API_URL}/api/branding/companies`).then(r => setCompanies(r.data || [])).catch(() => {});
      axios.get(`${API_URL}/api/warning-letters/offense-types`).then(r => {
        setOffenseTypes(r.data?.offense_types || []);
        setWarningTypes(r.data?.warning_types || []);
      }).catch(() => {});
    }
  }, [open, employee, loadWarnings]);

  const openAdd = () => { setForm(emptyForm()); setShowForm(true); };
  const openEdit = (w) => {
    setForm({
      id: w.id,
      issue_date: (w.issue_date || '').split('T')[0],
      company: w.company || 'stars_n_bars',
      warning_type: w.warning_type || w.severity || 'written',
      offense_types: w.offense_types || [],
      custom_offenses: w.custom_offenses || [],
      description: w.description || w.reason || '',
      plan_for_improvement: w.plan_for_improvement || '',
      disciplinary_action: w.disciplinary_action || '',
      manager: w.manager || '',
      issued_by: w.issued_by || '',
      suspension_enabled: !!w.suspension_enabled,
      suspension_days: w.suspension_days || '',
      suspension_from: (w.suspension_from || '').split('T')[0],
    });
    setShowForm(true);
  };

  const toggleOffense = (value) => {
    setForm(p => ({
      ...p,
      offense_types: p.offense_types.includes(value)
        ? p.offense_types.filter(v => v !== value)
        : [...p.offense_types, value],
    }));
  };

  const addCustomOffense = () => {
    const v = customInput.trim();
    if (v && !form.custom_offenses.includes(v)) {
      setForm(p => ({ ...p, custom_offenses: [...p.custom_offenses, v] }));
      setCustomInput('');
    }
  };

  const removeCustomOffense = (v) =>
    setForm(p => ({ ...p, custom_offenses: p.custom_offenses.filter(x => x !== v) }));

  const handleSave = async () => {
    if (!form.issue_date || !form.description.trim()) {
      toast({ title: 'Missing fields', description: 'Date and description are required', variant: 'destructive' });
      return;
    }
    if (form.suspension_enabled && (!form.suspension_days || parseInt(form.suspension_days, 10) < 1)) {
      toast({ title: 'Suspension days required', description: 'Enter the number of suspension days (min 1).', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      suspension_days: form.suspension_enabled ? parseInt(form.suspension_days, 10) : null,
      suspension_from: form.suspension_enabled ? (form.suspension_from || form.issue_date) : null,
    };
    try {
      if (form.id) {
        await axios.put(`${API_URL}/api/employees/${employee.id}/warnings/${form.id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/employees/${employee.id}/warnings`, payload);
      }
      toast({ title: 'Saved', description: 'Warning letter saved' });
      setShowForm(false);
      setForm(emptyForm());
      loadWarnings();
      if (onSuccess) onSuccess();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to save warning letter', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!warningToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/employees/${employee.id}/warnings/${warningToDelete.id}`);
      toast({ title: 'Deleted', description: 'Warning letter deleted' });
      loadWarnings();
      if (onSuccess) onSuccess();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    } finally {
      setDeleting(false);
      setWarningToDelete(null);
    }
  };

  const downloadPdf = (w) => {
    window.open(`${API_URL}/api/warning-letters/${employee.id}/${w.id}/pdf`, '_blank');
  };

  const [emailingId, setEmailingId] = useState(null);
  const emailWarning = async (w) => {
    setEmailingId(w.id);
    try {
      const res = await axios.post(`${API_URL}/api/warning-letters/${employee.id}/${w.id}/email`, {});
      toast({ title: 'Emailed', description: `Sent to ${res.data?.emailed_to || 'employee'}` });
      loadWarnings();
    } catch (e) {
      toast({ title: 'Email failed', description: e?.response?.data?.detail || 'Could not send email', variant: 'destructive' });
    } finally {
      setEmailingId(null);
    }
  };

  const isActive = (issueDate) => {
    const expiry = new Date(issueDate);
    expiry.setMonth(expiry.getMonth() + 6);
    return expiry > new Date();
  };
  const activeCount = warnings.filter(w => isActive(w.issue_date)).length;

  // Disciplinary history summary (this calendar year + all-time) for escalation context.
  const summary = useMemo(() => {
    const yr = new Date().getFullYear();
    const inYear = (d) => { try { return new Date(d).getFullYear() === yr; } catch { return false; } };
    const byType = { verbal: 0, written: 0, written2: 0, final: 0 };
    let warnYear = 0;
    warnings.forEach(w => {
      const wt = w.warning_type || w.severity || 'written';
      if (inYear(w.issue_date)) {
        warnYear += 1;
        if (byType[wt] != null) byType[wt] += 1;
      }
    });
    const suspYear = suspensions.filter(s => inYear(s.from_date));
    const suspDaysYear = suspYear.reduce((a, s) => a + (Number(s.days) || 0), 0);
    const emailed = warnings.filter(w => w.emailed_at).length;
    const acked = warnings.filter(w => w.acknowledged).length;
    return {
      yr, warnYear, byType,
      suspCountYear: suspYear.length, suspDaysYear,
      warnTotal: warnings.length, suspTotal: suspensions.length,
      emailed, acked,
      escalation: byType.final > 0 || warnYear >= 2,
    };
  }, [warnings, suspensions]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="warnings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Warning Letters — {employee?.name}
              {activeCount > 0 && <Badge className="bg-red-500 ml-2">{activeCount} Active</Badge>}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Disciplinary History summary — escalation at a glance */}
            <div className={`rounded-lg border p-3 ${summary.escalation ? 'border-red-300 bg-red-50/70' : 'border-gray-200 bg-gray-50'}`}
              data-testid="disciplinary-summary">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-semibold text-gray-700">Disciplinary History · {summary.yr}</div>
                {summary.escalation && (
                  <Badge className="bg-red-600 text-[10px]" data-testid="escalation-flag">⚠ Escalation pattern</Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
                <span data-testid="summary-warnings"><b>{summary.warnYear}</b> warning{summary.warnYear !== 1 ? 's' : ''}</span>
                <span className="text-gray-400">
                  ({summary.byType.verbal}V · {summary.byType.written + summary.byType.written2}W · {summary.byType.final}F)
                </span>
                <span data-testid="summary-suspensions"><b>{summary.suspCountYear}</b> suspension{summary.suspCountYear !== 1 ? 's' : ''} · <b>{summary.suspDaysYear}</b>d</span>
                {summary.emailed > 0 && (
                  <span className="text-emerald-600" data-testid="summary-acked">{summary.acked}/{summary.emailed} acknowledged</span>
                )}
              </div>
              {(summary.warnTotal > summary.warnYear || summary.suspTotal > summary.suspCountYear) && (
                <p className="text-[11px] text-gray-400 mt-1">
                  All-time: {summary.warnTotal} warning{summary.warnTotal !== 1 ? 's' : ''} · {summary.suspTotal} suspension{summary.suspTotal !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {!isViewer && !showForm && (
              <Button variant="outline" onClick={openAdd}
                className="w-full text-yellow-700 border-yellow-300 hover:bg-yellow-50"
                data-testid="issue-warning-btn">
                <Plus className="h-4 w-4 mr-1" /> Issue Warning Letter
              </Button>
            )}

            {showForm && !isViewer && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-4" data-testid="warning-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date *</Label>
                    <Input type="date" value={form.issue_date}
                      onChange={(e) => setForm(p => ({ ...p, issue_date: e.target.value }))}
                      data-testid="warning-date-input" />
                  </div>
                  <div>
                    <Label>Letterhead / Company</Label>
                    <Select value={form.company} onValueChange={(v) => setForm(p => ({ ...p, company: v }))}>
                      <SelectTrigger data-testid="warning-company-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {companies.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Type of Warning *</Label>
                  <Select value={form.warning_type} onValueChange={(v) => setForm(p => ({ ...p, warning_type: v }))}>
                    <SelectTrigger data-testid="warning-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(warningTypes.length ? warningTypes : Object.entries(WARNING_LABEL).map(([value, label]) => ({ value, label })))
                        .map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="mb-1.5 block">Type of Offense</Label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {offenseTypes.map(o => (
                      <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer"
                        data-testid={`offense-${o.value}`}>
                        <input type="checkbox" checked={form.offense_types.includes(o.value)}
                          onChange={() => toggleOffense(o.value)} />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </div>
                  {/* Custom / editable offense option */}
                  <div className="mt-2 flex gap-2">
                    <Input placeholder="Add a custom offense…" value={customInput}
                      onChange={(e) => setCustomInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomOffense(); } }}
                      className="flex-1" data-testid="custom-offense-input" />
                    <Button type="button" variant="outline" size="sm" onClick={addCustomOffense}
                      data-testid="add-custom-offense-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {form.custom_offenses.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {form.custom_offenses.map(c => (
                        <Badge key={c} variant="outline" className="bg-white gap-1">
                          {c}
                          <button type="button" onClick={() => removeCustomOffense(c)}><X className="h-3 w-3" /></button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label>Description of Violation *</Label>
                  <Textarea value={form.description} rows={4}
                    placeholder="Describe the incident / violation in detail…"
                    onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                    data-testid="warning-description-input" />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label>Plan for Improvement</Label>
                    <Textarea value={form.plan_for_improvement} rows={2}
                      onChange={(e) => setForm(p => ({ ...p, plan_for_improvement: e.target.value }))}
                      data-testid="warning-plan-input" />
                  </div>
                  <div>
                    <Label>Disciplinary Action Taken</Label>
                    <Textarea value={form.disciplinary_action} rows={2}
                      onChange={(e) => setForm(p => ({ ...p, disciplinary_action: e.target.value }))}
                      data-testid="warning-action-input" />
                  </div>
                </div>
                {/* Suspension — auto-creates a suspension record on the employee */}
                <div className="rounded-lg border border-red-200 bg-red-50/60 p-3">
                  <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    <input type="checkbox" checked={form.suspension_enabled}
                      onChange={(e) => setForm(p => ({ ...p, suspension_enabled: e.target.checked }))}
                      data-testid="warning-suspension-toggle" />
                    <span className="text-red-700">Apply suspension with this warning</span>
                  </label>
                  {form.suspension_enabled && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <Label>Suspension days *</Label>
                        <Input type="number" min="1" value={form.suspension_days}
                          placeholder="e.g. 3"
                          onChange={(e) => setForm(p => ({ ...p, suspension_days: e.target.value }))}
                          data-testid="warning-suspension-days" />
                      </div>
                      <div>
                        <Label>Suspension start date</Label>
                        <Input type="date" value={form.suspension_from || form.issue_date}
                          onChange={(e) => setForm(p => ({ ...p, suspension_from: e.target.value }))}
                          data-testid="warning-suspension-from" />
                      </div>
                      <p className="col-span-2 text-[11px] text-red-600">
                        A linked suspension record will be added to the employee automatically and counted as LOP (unpaid). It appears on the dashboard's Currently Suspended list.
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Manager</Label>
                    <Input value={form.manager}
                      onChange={(e) => setForm(p => ({ ...p, manager: e.target.value }))}
                      data-testid="warning-manager-input" />
                  </div>
                  <div>
                    <Label>Issued By</Label>
                    <Input value={form.issued_by}
                      onChange={(e) => setForm(p => ({ ...p, issued_by: e.target.value }))}
                      data-testid="warning-issued-by-input" />
                  </div>
                </div>

                {!form.id && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                    <input type="checkbox" checked={form.send_email}
                      onChange={(e) => setForm(p => ({ ...p, send_email: e.target.checked }))}
                      data-testid="warning-send-email-toggle" />
                    <span className="text-indigo-700">Email the warning letter PDF to the employee now (with portal acknowledgement link)</span>
                  </label>
                )}

                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => { setShowForm(false); setForm(emptyForm()); }}>Cancel</Button>
                  <Button type="button" size="sm" disabled={saving}
                    className="bg-yellow-600 hover:bg-yellow-700"
                    onClick={handleSave} data-testid="save-warning-btn">
                    {saving ? 'Saving…' : (form.id ? 'Update Warning' : 'Issue Warning')}
                  </Button>
                </div>
              </div>
            )}

            {/* List */}
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading…</div>
            ) : warnings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No warning letters recorded for this employee</div>
            ) : (
              <div className="space-y-3">
                {warnings
                  .slice()
                  .sort((a, b) => new Date(b.issue_date) - new Date(a.issue_date))
                  .map((w) => {
                    const active = isActive(w.issue_date);
                    const wt = w.warning_type || w.severity || 'written';
                    return (
                      <div key={w.id}
                        className={`border rounded-lg p-4 ${active ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200 opacity-80'}`}
                        data-testid={`warning-item-${w.id}`}>
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge className={`${WARNING_BADGES[wt] || 'bg-gray-500'} text-xs`}>
                                {WARNING_LABEL[wt] || wt}
                              </Badge>
                              <span className="text-sm font-medium">
                                {new Date(w.issue_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                              {active
                                ? <Badge variant="outline" className="text-xs text-yellow-700 border-yellow-400">Active</Badge>
                                : <Badge variant="outline" className="text-xs text-gray-500">Expired</Badge>}
                              {w.suspension_enabled && w.suspension_days && (
                                <Badge className="bg-red-600 text-xs" data-testid={`warning-suspension-badge-${w.id}`}>
                                  Suspended {w.suspension_days}d
                                </Badge>
                              )}
                              {w.emailed_at && (
                                <Badge variant="outline" className="text-xs text-indigo-600 border-indigo-300 gap-1">
                                  <MailCheck className="h-3 w-3" /> Emailed
                                </Badge>
                              )}
                              {w.acknowledged ? (
                                <Badge className="bg-emerald-600 text-xs gap-1" data-testid={`warning-ack-badge-${w.id}`}>
                                  <CheckCircle2 className="h-3 w-3" /> Acknowledged
                                </Badge>
                              ) : w.emailed_at ? (
                                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Awaiting acknowledgement</Badge>
                              ) : null}
                            </div>
                            {(w.offense_types?.length > 0 || w.custom_offenses?.length > 0) && (
                              <p className="text-xs text-gray-500 mb-1">
                                Offenses: {[...(w.offense_types || []), ...(w.custom_offenses || [])].join(', ')}
                              </p>
                            )}
                            <p className="text-sm text-gray-700">{w.description || w.reason}</p>
                            {w.issued_by && <p className="text-xs text-gray-500 mt-1">Issued by: {w.issued_by}</p>}
                            {(w.acknowledged || w.acknowledged_at) && (
                              <p className="text-xs text-emerald-600 mt-1">
                                Acknowledged by employee on {new Date(w.acknowledged_at).toLocaleString('en-GB')}
                                {w.acknowledgement_note ? ` — "${w.acknowledgement_note}"` : ''}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <Button variant="ghost" size="sm" onClick={() => downloadPdf(w)}
                              className="text-rose-600" data-testid={`download-warning-${w.id}`}>
                              <Download className="h-4 w-4" />
                            </Button>
                            {!isViewer && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => emailWarning(w)}
                                  disabled={emailingId === w.id} className="text-indigo-600"
                                  title="Email PDF to employee" data-testid={`email-warning-${w.id}`}>
                                  <Mail className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openEdit(w)}
                                  data-testid={`edit-warning-${w.id}`}>
                                  <Pencil className="h-4 w-4 text-gray-500" />
                                </Button>
                                <Button variant="ghost" size="sm"
                                  onClick={() => { setWarningToDelete(w); setDeleteConfirmOpen(true); }}
                                  className="text-red-500" data-testid={`delete-warning-${w.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            <div className="flex justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Warning Letter"
        description="Are you sure you want to delete this warning letter? This action cannot be undone."
        itemName={(warningToDelete?.description || warningToDelete?.reason || '').substring(0, 50)}
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </>
  );
};

export default WarningsDialog;
